
import React, { useState, useCallback, useEffect } from 'react'
import { StageWrapper } from '@/features/enrollment/core/StageWrapper'
import type { ValidationErrors } from '@/features/enrollment'
import { EnrollmentApi } from '@/features/enrollment/services/enrollment.service'
import { CheckCircle2, AlertCircle, Loader2, ArrowLeft, ArrowRight } from 'lucide-react'

interface ComplianceStepProps {
    enrollmentData: any
    updateEnrollmentData: (field: string, value: any) => void
    handleNavigate: (targetIndex: number) => void | Promise<void>
    handleSimpleNavigate?: (targetIndex: number) => void
    handleNavigateWithCompletion?: (targetIndex: number) => Promise<void>
    isNavigating: boolean
    currentStepIndex?: number
    totalSteps?: number
    onComplete: () => void
    sessionId?: string
    [key: string]: any
}

export default function ComplianceStep({
    enrollmentData,
    updateEnrollmentData,
    handleNavigate,
    handleSimpleNavigate,
    handleNavigateWithCompletion,
    isNavigating,
    currentStepIndex,
    totalSteps = 6,
    onComplete,
    sessionId
}: ComplianceStepProps) {
    const [errors, setErrors] = useState<ValidationErrors>({})
    const [isSubmitting, setIsSubmitting] = useState(false)
    const [submitError, setSubmitError] = useState<string | null>(null)
    const [isSubmitted, setIsSubmitted] = useState(false)

    // Auto-fill compliance fields with default values on mount
    useEffect(() => {
        // Question 1: Default to CMS Call completed
        if (!enrollmentData.authorizedUserConsent) {
            updateEnrollmentData('authorizedUserConsent', 'CMS Call Was Completed And Client Gave Authorization')
        }
        // Question 2: Default to Yes for permission
        if (!enrollmentData.permissionGiven) {
            updateEnrollmentData('permissionGiven', 'Yes')
        }
        // Question 3: Default to No other insurance
        if (!enrollmentData.hasInsurance) {
            updateEnrollmentData('hasInsurance', 'No, I do not have any other insurance such as Medicare, Medicaid, Tricare, or Employer Provided Insurance')
        }
        // Question 4: Default to YES for terms
        if (!enrollmentData.termsAgreement) {
            updateEnrollmentData('termsAgreement', 'YES')
        }
    }, []) // Run once on mount

    const handleSubmit = useCallback(async () => {
        const validationErrors: ValidationErrors = {}

        if (!enrollmentData.authorizedUserConsent) {
            validationErrors.authorizedUserConsent = 'Please select CMS authorization status'
        }
        if (!enrollmentData.permissionGiven) {
            validationErrors.permissionGiven = 'Please confirm permission'
        }
        if (!enrollmentData.hasInsurance) {
            validationErrors.hasInsurance = 'Please select insurance enrollment status'
        }
        if (!enrollmentData.termsAgreement) {
            validationErrors.termsAgreement = 'Please agree to terms and conditions'
        }

        setErrors(validationErrors)

        if (Object.keys(validationErrors).length > 0) {
            console.warn('⚠️ ComplianceStep validation failed:', validationErrors)
            return
        }

        try {
            setIsSubmitting(true)
            setSubmitError(null)

            if (!sessionId) {
                throw new Error('Session ID is missing — please restart the enrollment')
            }

            // Build final submission data with current form values
            const submissionData = {
                ...enrollmentData,
                authorizedUserConsent: enrollmentData.authorizedUserConsent,
                permissionGiven: enrollmentData.permissionGiven,
                hasInsurance: enrollmentData.hasInsurance,
                termsAgreement: enrollmentData.termsAgreement,
            }

            console.log('🚀 Submitting to JotForm — sessionId:', sessionId)
            console.log('📋 Top-level keys:', Object.keys(submissionData).join(', '))

            await EnrollmentApi.submitACAForm(sessionId, submissionData)
            console.log('✅ JotForm submission successful')

            setIsSubmitted(true)

            // Auto-navigate to next step (Follow-Up)
            setTimeout(() => {
                if (handleNavigateWithCompletion && currentStepIndex !== undefined) {
                    handleNavigateWithCompletion(currentStepIndex + 1)
                } else {
                    handleNavigate(5)
                }
            }, 1500)

        } catch (error) {
            console.error('❌ JotForm submission failed:', error)
            setSubmitError(error instanceof Error ? error.message : 'Failed to submit. Please try again.')
        } finally {
            setIsSubmitting(false)
        }
    }, [enrollmentData, sessionId, handleNavigate, handleNavigateWithCompletion, currentStepIndex])


    const handleBack = useCallback(() => {
        if (currentStepIndex !== undefined && currentStepIndex > 0) {
            if (handleSimpleNavigate) {
                handleSimpleNavigate(currentStepIndex - 1)
            } else {
                handleNavigate(currentStepIndex - 1)
            }
        }
    }, [currentStepIndex, handleSimpleNavigate, handleNavigate])

    return (
        <StageWrapper
            currentStepIndex={currentStepIndex ?? 4}
            totalSteps={totalSteps}
            handleNavigate={handleNavigate}
            handleNavigateWithCompletion={handleNavigateWithCompletion}
            isNavigating={isNavigating}
            showNextButton={false}
            customButtons={
                <div className="flex justify-between">
                    <button
                        onClick={handleBack}
                        disabled={isNavigating || isSubmitting}
                        className="btn-outline flex items-center space-x-2 min-w-[100px] justify-center"
                    >
                        <ArrowLeft className="w-4 h-4" />
                        <span>Back</span>
                    </button>
                    <button
                        onClick={handleSubmit}
                        disabled={isNavigating || isSubmitting || isSubmitted}
                        className="btn-primary flex items-center space-x-2 min-w-[200px] justify-center"
                    >
                        {isSubmitting ? (
                            <>
                                <Loader2 className="w-4 h-4 animate-spin" />
                                <span>Submitting...</span>
                            </>
                        ) : isSubmitted ? (
                            <>
                                <CheckCircle2 className="w-4 h-4" />
                                <span>Proceeding to Follow-Up...</span>
                            </>
                        ) : (
                            <>
                                <span>Complete Enrollment</span>
                                <ArrowRight className="w-4 h-4" />
                            </>
                        )}
                    </button>
                </div>
            }
        >
            <div className="card text-left">
                {/* Submission Status */}
                {isSubmitting && (
                    <div className="mb-6 p-4 bg-indigo-50 border border-indigo-200 rounded-lg flex items-center space-x-3 animate-pulse">
                        <Loader2 className="w-5 h-5 text-indigo-600 animate-spin" />
                        <span className="text-indigo-700 font-medium">Submitting enrollment...</span>
                    </div>
                )}

                {submitError && (
                    <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-start space-x-3">
                        <AlertCircle className="w-5 h-5 text-red-600 mt-0.5" />
                        <div className="flex-1">
                            <span className="text-red-700 font-bold block mb-1">Submission Failed</span>
                            <span className="text-red-600 text-sm">{submitError}</span>
                            <p className="mt-2 text-xs text-red-500 italic">Click "Complete Enrollment" to retry.</p>
                        </div>
                    </div>
                )}

                {isSubmitted && (
                    <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg flex items-center space-x-3">
                        <CheckCircle2 className="w-5 h-5 text-green-600" />
                        <span className="text-green-700 font-medium">Enrollment submitted! Redirecting to Follow-Up...</span>
                    </div>
                )}

                <div className="flex items-center space-x-3 mb-6">
                    <div className="w-12 h-12 bg-secondary-100 rounded-lg flex items-center justify-center">
                        <svg className="w-6 h-6 text-secondary-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                        </svg>
                    </div>
                    <div>
                        <h3 className="text-xl font-semibold text-accent-900">Compliance & Authorization</h3>
                        <p className="text-sm text-accent-600">Final CMS authorization and permissions.</p>
                    </div>
                </div>

                <div className="space-y-6">
                    {/* CMS Call Compliance */}
                    <div>
                        <h4 className="text-md font-semibold text-accent-800 mb-4">CMS Authorization</h4>
                        <div className="p-4 rounded-md border border-gray-200 text-sm text-gray-800 mb-4">
                            <p className="font-bold mb-2">COMPLIANCE: Call CMS Hotline 800-318-2596 With The Customer On The Phone and Say The Following:</p>
                            <div className="bg-gray-50 border-l-4 border-gray-400 pl-4 py-3 my-4">
                                <p className="font-medium text-base leading-relaxed text-gray-900 italic">
                                    "The customer gives approval to Health Connect Marketing and its licensed agents Anthony Ciponaer (NPN: 20867414) and Ashley Harmer (NPN: 22126690) to be the Authorized User & Authorized Agent to speak on the client's behalf for 365 Days. The client also wishes to revoke all other authorized users"
                                </p>
                            </div>
                            <p className="italic font-medium text-gray-600">Make sure the CMS customer support spells the Agent's name correctly.</p>
                        </div>

                        {/* Compliance Fields - Visible for agent to fill */}
                        <div className="space-y-6 mt-6">
                            {/* Authorized User Consent For 365 Days */}
                            <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
                                <label className="block text-sm font-semibold text-gray-800 mb-3">
                                    Authorized User Consent For 365 Days <span className="text-red-500">*</span>
                                </label>
                                <div className="space-y-2">
                                    <label className="flex items-center p-3 border rounded-lg cursor-pointer hover:bg-white transition-colors">
                                        <input
                                            type="radio"
                                            name="authorizedUserConsent"
                                            value="CMS Call Was Completed And Client Gave Authorization"
                                            checked={enrollmentData.authorizedUserConsent === 'CMS Call Was Completed And Client Gave Authorization'}
                                            onChange={(e) => updateEnrollmentData('authorizedUserConsent', e.target.value)}
                                            className="focus:ring-indigo-500 h-4 w-4 text-indigo-600 border-gray-300"
                                        />
                                        <span className="ml-3 text-sm font-medium text-gray-700">CMS Call Was Completed And Client Gave Authorization</span>
                                    </label>
                                    <label className="flex items-center p-3 border rounded-lg cursor-pointer hover:bg-yellow-50 transition-colors border-yellow-200 bg-yellow-50">
                                        <input
                                            type="radio"
                                            name="authorizedUserConsent"
                                            value="CMS CALL Will be completed later"
                                            checked={enrollmentData.authorizedUserConsent === 'CMS CALL Will be completed later'}
                                            onChange={(e) => updateEnrollmentData('authorizedUserConsent', e.target.value)}
                                            className="focus:ring-yellow-500 h-4 w-4 text-yellow-600 border-gray-300"
                                        />
                                        <span className="ml-3 text-sm font-medium text-yellow-800">CMS CALL Will be completed later</span>
                                    </label>
                                </div>
                                {errors.authorizedUserConsent && <p className="mt-2 text-sm text-red-600">{errors.authorizedUserConsent}</p>}
                            </div>

                            {/* Permission with Highlighted Agent Names */}
                            <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
                                <label className="block text-sm font-semibold text-gray-800 mb-3">
                                    I give Health Connect Marketing and its Licensed Agents{' '}
                                    <span className="bg-yellow-200 px-1">Anthony Ciponaer</span>
                                    {' '}and{' '}
                                    <span className="bg-yellow-200 px-1">Ashley Harmer</span>
                                    {' '}permission to be my authorized user for 365 days and agent of record on my current plan or switch my plan to a different carrier or plan. <span className="text-red-500">*</span>
                                </label>
                                <div className="flex space-x-6">
                                    <label className="flex items-center p-3 border rounded-lg cursor-pointer hover:bg-white transition-colors">
                                        <input
                                            type="radio"
                                            name="permissionGiven"
                                            value="Yes"
                                            checked={enrollmentData.permissionGiven === 'Yes'}
                                            onChange={() => updateEnrollmentData('permissionGiven', 'Yes')}
                                            className="form-radio h-5 w-5 text-indigo-600"
                                        />
                                        <span className="ml-2 text-gray-700 font-medium">Yes</span>
                                    </label>
                                </div>
                                {errors.permissionGiven && <p className="mt-2 text-sm text-red-600">{errors.permissionGiven}</p>}
                            </div>

                            {/* Insurance Enrollment Status */}
                            <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
                                <label className="block text-sm font-semibold text-gray-800 mb-3">
                                    Insurance Enrollment Status <span className="text-red-500">*</span>
                                </label>
                                <div className="space-y-2">
                                    <label className="flex items-center p-3 border rounded-lg cursor-pointer hover:bg-blue-50 transition-colors border-blue-200 bg-blue-50">
                                        <input
                                            type="radio"
                                            name="hasInsurance"
                                            value="No, I do not have any other insurance such as Medicare, Medicaid, Tricare, or Employer Provided Insurance"
                                            checked={enrollmentData.hasInsurance === 'No, I do not have any other insurance such as Medicare, Medicaid, Tricare, or Employer Provided Insurance'}
                                            onChange={(e) => updateEnrollmentData('hasInsurance', e.target.value)}
                                            className="focus:ring-blue-500 h-4 w-4 text-blue-600 border-gray-300"
                                        />
                                        <span className="ml-3 text-sm font-medium text-blue-800">No, I do not have any other insurance such as Medicare, Medicaid, Tricare, or Employer Provided Insurance</span>
                                    </label>
                                </div>
                                {errors.hasInsurance && <p className="mt-2 text-sm text-red-600">{errors.hasInsurance}</p>}
                            </div>

                            {/* Terms Agreement */}
                            <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
                                <label className="block text-sm font-semibold text-gray-800 mb-3">
                                    I agree to the terms and conditions of this enrollment. <span className="text-red-500">*</span>
                                </label>
                                <div className="flex space-x-6">
                                    <label className="flex items-center p-3 border rounded-lg cursor-pointer hover:bg-white transition-colors">
                                        <input
                                            type="radio"
                                            name="termsAgreement"
                                            value="YES"
                                            checked={enrollmentData.termsAgreement === 'YES'}
                                            onChange={() => updateEnrollmentData('termsAgreement', 'YES')}
                                            className="form-radio h-5 w-5 text-indigo-600"
                                        />
                                        <span className="ml-2 text-gray-700 font-medium">YES</span>
                                    </label>
                                </div>
                                {errors.termsAgreement && <p className="mt-2 text-sm text-red-600">{errors.termsAgreement}</p>}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </StageWrapper>
    )
}
